import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ChevronDown, Users } from 'lucide-react';
import { useCountUp } from '../hooks/useCountUp';
import { AudioPreview } from './AudioPreview';
export default function HeroSection() {
  const {
    count,
    ref
  } = useCountUp(612, 2000, true);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.6
      }
    }
  };
  const itemFade = {
    hidden: {
      opacity: 0,
      y: 12,
      filter: 'blur(4px)'
    },
    show: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  const itemHighlight = {
    hidden: {
      opacity: 0,
      scale: 1.08,
      filter: 'blur(6px)'
    },
    show: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  const lines = [{
    text: 'Te acuestas cansado.',
    variant: itemFade
  }, {
    text: 'Apagas la luz.',
    variant: itemFade
  }, {
    text: 'Y justo ahí… tu cabeza decide que es el mejor momento para repasar tu vida completa.',
    variant: itemFade
  }, {
    text: 'Pendientes. Conversaciones. Dinero. Salud. Trabajo. Culpa.',
    variant: itemFade
  }, {
    text: 'Y la frase que lo empeora todo:',
    variant: itemFade
  }, {
    text: '«Tengo que dormir ya.»',
    variant: itemHighlight,
    highlight: true
  }, {
    text: '«Si no duermo, mañana me arruino.»',
    variant: itemHighlight,
    highlight: true
  }, {
    text: '«¿Por qué algo tan básico se me complica?»',
    variant: itemHighlight,
    highlight: true
  }, {
    text: 'No te falta disciplina.',
    variant: itemFade
  }, {
    text: 'Te falta un protocolo para cuando tu mente no se calla.',
    variant: itemFade
  }, {
    text: 'Un sistema que desactive el circuito nocturno.',
    variant: itemFade,
    glow: true
  }];
  const scrollToPricing = () => {
    document.getElementById('comprar')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex flex-col justify-center items-center bg-gradient-to-b from-brand-dark via-brand-navy to-brand-charcoal overflow-hidden pt-6 pb-16 px-4 sm:px-6 lg:px-8 vignette" data-id="element-160">
      {/* Star-like particles */}
      {[...Array(15)].map((_, i) => <motion.div key={i} className="absolute rounded-full pointer-events-none" style={{
      width: 2 + Math.random() * 3,
      height: 2 + Math.random() * 3,
      background: i % 3 === 0 ? 'rgba(67, 56, 202, 0.6)' // Indigo
      : i % 3 === 1 ? 'rgba(217, 119, 6, 0.5)' // Amber
      : 'rgba(255, 255, 255, 0.4)',
      filter: 'blur(0.5px)',
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`
    }} animate={{
      opacity: [0.2, 0.8, 0.2],
      scale: [1, 1.2, 1]
    }} transition={{
      duration: 3 + Math.random() * 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: Math.random() * 5
    }} data-id="element-161" />)}

      {/* Soft atmospheric glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand-indigo/[0.08] blur-[120px] rounded-full pointer-events-none" data-id="element-162" />
      <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-brand-amber/[0.03] blur-[100px] rounded-full pointer-events-none" data-id="element-163" />

      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-164">
        <div className="order-1" data-id="element-165">
          <motion.div initial={{
          opacity: 0,
          y: 40,
          filter: 'blur(10px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} transition={{
          duration: 1.2,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="mb-8" data-id="element-166">
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl leading-tight text-white mb-2 uppercase tracking-wide" data-id="element-167">
              Si tu mente se prende cuando apagas la luz:
            </h1>
            <motion.p initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5,
            duration: 1,
            ease: 'easeOut'
          }} className="font-heading text-3xl sm:text-4xl text-brand-indigo italic tracking-wide" data-id="element-168">
              esto es para ti.
            </motion.p>
          </motion.div>

          <motion.div variants={container} initial="hidden" animate="show" className="space-y-3 mb-10" data-id="element-169">
            {lines.map((line, index) => <motion.p key={index} variants={line.variant} className={`text-lg sm:text-xl leading-relaxed ${line.highlight ? 'text-brand-amber font-medium italic' : line.glow ? 'text-white font-bold text-glow-amber' : 'text-slate-400'}`} data-id="element-170">
                {line.text}
              </motion.p>)}
          </motion.div>

          {/* Image - MOBILE ONLY */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.85,
          filter: 'blur(15px)'
        }} animate={{
          opacity: 1,
          scale: 1,
          filter: 'blur(0px)'
        }} transition={{
          duration: 1.4,
          delay: 0.5,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="flex justify-center mb-8 lg:hidden" data-id="element-171">
            <div className="relative animate-float" data-id="element-172">
              <div className="absolute -inset-6 bg-brand-indigo/10 blur-[50px] rounded-full" data-id="element-173" />
              <img src="https://cdn.magicpatterns.com/uploads/4vut6SFKDkiGXHBcSk84Zt/APAGA_LA_MENTE_COVER.png" alt="APAGA LA MENTE Tablet Mockup" className="relative w-full max-w-sm h-auto drop-shadow-2xl rounded-lg" data-id="element-174" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30,
          filter: 'blur(8px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} transition={{
          delay: 2.8,
          duration: 0.8,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="flex flex-col gap-3 items-start" data-id="element-175">
            <AudioPreview data-id="element-176" />

            <Button size="xl" pulse={true} onClick={scrollToPricing} data-id="element-177">
              QUIERO APAGAR MI MENTE ESTA NOCHE →
            </Button>

            <div className="flex flex-col gap-1 ml-2" data-id="element-178">
              <span className="text-slate-400 text-sm" data-id="element-179">
                Por solo $6.39 — Acceso inmediato
              </span>
              <span className="text-slate-500 text-xs" data-id="element-180">
                ✓ Si no funciona, te devolvemos todo en 7 días. Sin preguntas.
              </span>
            </div>

            {/* Social Proof Counter */}
            <div className="flex items-center gap-2 mt-3 bg-brand-navy/50 backdrop-blur-sm px-4 py-2 rounded-full border border-brand-indigo/20" ref={ref} data-id="element-181">
              <Users size={16} className="text-brand-indigo" data-id="element-182" />
              <span className="text-slate-400 text-sm font-medium" data-id="element-183">
                <span className="font-bold text-brand-indigo text-lg mr-1 tabular-nums" data-id="element-184">
                  {count}
                </span>
                personas ya recuperaron sus noches
              </span>
            </div>
          </motion.div>
        </div>

        {/* Image - DESKTOP ONLY */}
        <motion.div initial={{
        opacity: 0,
        x: 60,
        scale: 0.9,
        filter: 'blur(15px)'
      }} animate={{
        opacity: 1,
        x: 0,
        scale: 1,
        filter: 'blur(0px)'
      }} transition={{
        duration: 1.5,
        delay: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94]
      }} className="order-2 hidden lg:flex justify-center" data-id="element-185">
          <div className="relative animate-float" data-id="element-186">
            <div className="absolute -inset-8 bg-brand-indigo/10 blur-[60px] rounded-full" data-id="element-187" />
            <img src="https://cdn.magicpatterns.com/uploads/4vut6SFKDkiGXHBcSk84Zt/APAGA_LA_MENTE_COVER.png" alt="APAGA LA MENTE Book Mockup" className="relative w-full max-w-md lg:max-w-full h-auto drop-shadow-2xl rounded-lg transform rotate-1 hover:rotate-0 transition-transform duration-700" data-id="element-188" />
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1,
      y: [0, 10, 0]
    }} transition={{
      delay: 3.5,
      duration: 2.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-brand-indigo/30" data-id="element-189">
        <ChevronDown size={32} data-id="element-190" />
      </motion.div>
    </section>;
}